// Property Management → Online Payments (Stripe).
//
//   Payments   every online payment and where it stands; retry a receipt that could not
//              post; charge the lease's returned-payment (NSF) fee after a return
//   Top-ups    what the operating account owes the trust account because Stripe netted
//              its fee out of a rent payout (or trust money that is really a convenience
//              fee) — mark each transferred; until then Month-End warns
//   Payouts    Stripe's payouts to the trust account, for the bank reconciliation
//   Holds      leases whose bank payments are held after a return or dispute
//   Settings   the switch (off until the managing broker turns it on), cards, the card
//              convenience fee, autopay, and which trust account Stripe pays out to
//
// The server decides who may do what (settings, transfers and holds are the managing
// broker's — 68 IAC 1450.705); the buttons only explain it.

import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import {
    PageHeader, Panel, Tabs, Stat, Pill, Table, Modal, Input, Select,
    Button, Notice, Loading, money, fmtDate, titleCase,
} from '../../components/secure/pm/PMKit';

const API = '/api/property-management/online-payments';
const pad = (n) => String(n).padStart(2, '0');
const d = new Date();
const TODAY = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const PAY_TONE = { succeeded: 'good', processing: 'warn', created: 'slate', failed: 'bad', returned: 'bad', canceled: 'slate', posting_failed: 'bad' };
const errNotice = (e) => ({ tone: 'bad', message: e.message, problems: e.payload?.problems });

export default function PMOnlinePaymentsPage() {
    const api = useApiClient();
    const isMB = !!getCurrentUser()?.isManagingBroker;
    const [tab, setTab] = useState(new URLSearchParams(window.location.search).get('tab') || 'payments');
    const [summary, setSummary] = useState(null);
    const [notice, setNotice] = useState(null);

    const loadSummary = useCallback(() => {
        api.get(`${API}/summary`).then(setSummary).catch((e) => setNotice(errNotice(e)));
    }, [api]);
    useEffect(() => { loadSummary(); }, [loadSummary]);

    if (!summary) return notice ? <Notice {...notice} /> : <Loading />;
    const s = summary.settings;
    const t = summary.topups_due;

    return (
        <div>
            <PageHeader title="Online Payments"
                subtitle="Tenant rent by bank debit (and card, if switched on) through Stripe. Receipts post to the trust journal only when the money has actually arrived." />
            {notice && <Notice {...notice} onDismiss={() => setNotice(null)} />}
            {!s.enabled && (
                <Notice tone="info" title="Online payments are switched off"
                    message={isMB ? 'Enter the Stripe keys at Administration → Integrations, choose the payout trust account under Settings, then switch payments on.'
                        : 'The managing broker turns online payments on under Settings.'} />
            )}
            {!summary.stripe_configured && (
                <Notice tone="warn" title="Stripe is not set up"
                    message="The secret key and the webhook signing secret are entered at Administration → Integrations → Stripe." />
            )}
            {summary.warnings.length > 0 && <Notice tone="warn" title="Needs attention" problems={summary.warnings} />}

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <Stat label="Rent received online (30 days)" value={money(summary.counts.rent_30d)} sub={`${summary.counts.succeeded_30d} payment(s)`} />
                <Stat label="Bank payments clearing" value={summary.counts.processing} />
                <Stat label="Operating owes trust" value={money(t.operating_to_trust)} tone={t.operating_to_trust > 0 ? 'warn' : 'default'} onClick={() => setTab('topups')} />
                <Stat label="Trust holds brokerage fees" value={money(t.trust_to_operating)} tone={t.trust_to_operating > 0 ? 'warn' : 'default'} onClick={() => setTab('topups')} />
                <Stat label="Autopay enrolments" value={summary.counts.autopay_active} />
            </div>

            <Tabs tabs={[
                { key: 'payments', label: 'Payments' },
                { key: 'topups', label: `Trust top-ups${t.count ? ` (${t.count})` : ''}` },
                { key: 'payouts', label: 'Payouts' },
                { key: 'holds', label: `Holds${summary.counts.holds ? ` (${summary.counts.holds})` : ''}` },
                { key: 'settings', label: 'Settings' },
            ]} active={tab} onChange={(k) => { setTab(k); setNotice(null); }} />
            {tab === 'payments' && <PaymentsTab api={api} setNotice={setNotice} reload={loadSummary} />}
            {tab === 'topups' && <TopupsTab api={api} isMB={isMB} setNotice={setNotice} reload={loadSummary} />}
            {tab === 'payouts' && <PayoutsTab api={api} setNotice={setNotice} />}
            {tab === 'holds' && <HoldsTab api={api} isMB={isMB} setNotice={setNotice} reload={loadSummary} />}
            {tab === 'settings' && <SettingsTab api={api} isMB={isMB} summary={summary} setNotice={setNotice} reload={loadSummary} />}
        </div>
    );
}

function PaymentsTab({ api, setNotice, reload }) {
    const [status, setStatus] = useState('');
    const [rows, setRows] = useState(null);
    const load = useCallback(() => {
        api.get(`${API}/payments${status ? `?status=${status}` : ''}`).then(setRows).catch((e) => setNotice(errNotice(e)));
    }, [api, status, setNotice]);
    useEffect(() => { load(); }, [load]);

    const act = async (path, okMsg) => {
        try {
            await api.post(path, {});
            setNotice({ tone: 'good', message: okMsg });
            load(); reload();
        } catch (e) { setNotice(errNotice(e)); }
    };

    return (
        <Panel title="Payments" actions={
            <Select value={status} onChange={(e) => setStatus(e.target.value)} className="mb-0"
                options={[{ value: '', label: 'All statuses' }, 'processing', 'succeeded', 'posting_failed', 'returned', 'failed', 'canceled', 'created']} />
        }>
            {!rows ? <Loading /> : (
                <Table csvName="online-payments" rows={rows} empty="No online payments yet."
                    rowTone={(r) => (r.status === 'posting_failed' || r.status === 'returned' ? 'bad' : null)}
                    columns={[
                        { key: 'payment_id', label: '#' },
                        { key: 'created_at', label: 'Started', nowrap: true, render: (r) => fmtDate(r.created_at) },
                        { key: 'property_name', label: 'Property / unit', render: (r) => `${r.property_name} · ${r.unit_number}` },
                        { key: 'tenant_name', label: 'Tenant', render: (r) => r.tenant_name || '—' },
                        { key: 'method', label: 'Method', render: (r) => (r.method === 'ach' ? 'Bank' : 'Card') + (r.purpose === 'autopay' ? ' (auto)' : '') },
                        { key: 'rent_amount', label: 'Rent (trust)', align: 'right', render: (r) => money(r.rent_amount) },
                        { key: 'convenience_fee', label: 'Conv. fee', align: 'right', render: (r) => money(r.convenience_fee) },
                        { key: 'stripe_fee', label: 'Stripe fee', align: 'right', render: (r) => (r.stripe_fee === null ? '—' : `${money(r.stripe_fee)}${r.fee_estimated ? ' (est.)' : ''}`) },
                        { key: 'status', label: 'Status', render: (r) => <Pill tone={PAY_TONE[r.status] || 'slate'}>{titleCase(r.status)}</Pill> },
                        { key: 'failure_reason', label: 'Note', render: (r) => r.failure_reason || (r.receipt_entry_id ? `Receipt entry ${r.receipt_entry_id}` : '—') },
                        { key: 'actions', label: '', render: (r) => (
                            <div className="flex gap-1">
                                {r.status === 'posting_failed' && (
                                    <Button variant="ghost" onClick={() => act(`${API}/payments/${r.payment_id}/retry-posting`, 'Receipt posted to the trust journal.')}>Retry posting</Button>
                                )}
                                {r.status === 'returned' && !r.nsf_billing_id && Number(r.nsf_fee) > 0 && (
                                    <Button variant="ghost" onClick={() => act(`${API}/payments/${r.payment_id}/nsf-fee`, 'Returned-payment fee charged to the lease.')}>
                                        Charge NSF fee {money(r.nsf_fee)}
                                    </Button>
                                )}
                            </div>
                        ) },
                    ]} />
            )}
        </Panel>
    );
}

function TopupsTab({ api, isMB, setNotice, reload }) {
    const [status, setStatus] = useState('due');
    const [rows, setRows] = useState(null);
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({ transferred_on: TODAY, reference: '' });
    const load = useCallback(() => {
        api.get(`${API}/topups?status=${status}`).then(setRows).catch((e) => setNotice(errNotice(e)));
    }, [api, status, setNotice]);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        try {
            await api.post(`${API}/topups/${modal.topup_id}/transferred`, form);
            setNotice({ tone: 'good', message: 'Transfer recorded on the brokerage books.' });
            setModal(null); load(); reload();
        } catch (e) { setNotice(errNotice(e)); }
    };

    return (
        <Panel title="Trust top-ups"
            subtitle="Stripe pays the trust account rent + convenience fee − its own fee, while the trust journal records the full rent. The difference is brokerage money and must be moved: operating → trust for fees, trust → operating for convenience fees.">
            <Select value={status} onChange={(e) => setStatus(e.target.value)}
                options={[{ value: 'due', label: 'Due' }, { value: 'transferred', label: 'Transferred' }, { value: '', label: 'All' }]} />
            {!rows ? <Loading /> : (
                <Table csvName="trust-topups" rows={rows} empty="Nothing here."
                    columns={[
                        { key: 'topup_id', label: '#' },
                        { key: 'created_at', label: 'Raised', nowrap: true, render: (r) => fmtDate(r.created_at) },
                        { key: 'payment_id', label: 'Payment', render: (r) => (r.payment_id ? `#${r.payment_id}` : '—') },
                        { key: 'kind', label: 'Why', render: (r) => (r.kind === 'return_fee' ? 'Return / dispute fee' : 'Processing fee') },
                        { key: 'stripe_fee', label: 'Stripe fee', align: 'right', render: (r) => money(r.stripe_fee) },
                        { key: 'convenience_fee', label: 'Conv. fee', align: 'right', render: (r) => money(r.convenience_fee) },
                        { key: 'direction', label: 'Move', render: (r) => (r.direction === 'operating_to_trust' ? 'Operating → trust' : 'Trust → operating') },
                        { key: 'amount', label: 'Amount', align: 'right', render: (r) => money(r.amount) },
                        { key: 'status', label: 'Status', render: (r) => <Pill tone={r.status === 'due' ? 'warn' : 'good'}>{titleCase(r.status)}</Pill> },
                        { key: 'transfer_reference', label: 'Reference', render: (r) => (r.transferred_on ? `${fmtDate(r.transferred_on)} ${r.transfer_reference || ''}` : '—') },
                        { key: 'act', label: '', render: (r) => (r.status === 'due' && isMB
                            ? <Button variant="ghost" onClick={() => { setForm({ transferred_on: TODAY, reference: '' }); setModal(r); }}>Mark transferred</Button>
                            : null) },
                    ]} />
            )}
            {!isMB && <p className="text-xs text-slate-500 mt-2">Only the managing broker can mark a transfer made.</p>}
            {modal && (
                <Modal title={`Top-up #${modal.topup_id}: ${money(modal.amount)}`}
                    subtitle={modal.direction === 'operating_to_trust' ? 'Transfer from the operating account to the trust account.' : 'Transfer from the trust account to the operating account.'}
                    onClose={() => setModal(null)}>
                    <Input label="Date of transfer" type="date" value={form.transferred_on} onChange={(e) => setForm({ ...form, transferred_on: e.target.value })} />
                    <Input label="Bank reference / check number" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
                    <div className="flex gap-2"><Button onClick={save}>Record transfer</Button><Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button></div>
                </Modal>
            )}
        </Panel>
    );
}

function PayoutsTab({ api, setNotice }) {
    const [rows, setRows] = useState(null);
    useEffect(() => { api.get(`${API}/payouts`).then(setRows).catch((e) => setNotice(errNotice(e))); }, [api, setNotice]);
    return (
        <Panel title="Stripe payouts" subtitle="What Stripe sent to the trust account. Use these when reconciling the trust bank statement: each payout is the day's rent, plus convenience fees, less Stripe's fees.">
            {!rows ? <Loading /> : (
                <Table csvName="stripe-payouts" rows={rows} empty="No payouts yet."
                    columns={[
                        { key: 'payout_id', label: 'Payout' },
                        { key: 'arrival_date', label: 'Arrives', nowrap: true, render: (r) => fmtDate(r.arrival_date) },
                        { key: 'amount', label: 'Amount', align: 'right', render: (r) => money(r.amount) },
                        { key: 'status', label: 'Status', render: (r) => <Pill tone={r.status === 'paid' ? 'good' : r.status === 'failed' ? 'bad' : 'slate'}>{titleCase(r.status)}</Pill> },
                        { key: 'failure_message', label: 'Note', render: (r) => r.failure_message || '—' },
                    ]} />
            )}
        </Panel>
    );
}

function HoldsTab({ api, isMB, setNotice, reload }) {
    const [rows, setRows] = useState(null);
    const [fees, setFees] = useState({});
    const load = useCallback(() => { api.get(`${API}/holds`).then(setRows).catch((e) => setNotice(errNotice(e))); }, [api, setNotice]);
    useEffect(() => { load(); }, [load]);
    const clear = async (id) => {
        if (!window.confirm('Lift the hold? The tenant will be able to pay by bank again.')) return;
        try { await api.post(`${API}/leases/${id}/clear-hold`, {}); setNotice({ tone: 'good', message: 'Hold lifted.' }); load(); reload(); }
        catch (e) { setNotice(errNotice(e)); }
    };
    const saveFee = async (id) => {
        try { await api.put(`${API}/leases/${id}/nsf-fee`, { nsf_fee: fees[id] ?? '' }); setNotice({ tone: 'good', message: 'Returned-payment fee saved on the lease.' }); load(); }
        catch (e) { setNotice(errNotice(e)); }
    };
    return (
        <Panel title="Holds after a returned or disputed payment"
            subtitle="The receipt was reversed in the trust journal and the owner's book, and the charges reopened. Bank payments and autopay stay off for the lease until the managing broker lifts the hold.">
            {!rows ? <Loading /> : (
                <Table rows={rows} empty="No holds." toolbar={false}
                    columns={[
                        { key: 'property_name', label: 'Property / unit', render: (r) => `${r.property_name} · ${r.unit_number}` },
                        { key: 'online_payment_hold', label: 'Reason' },
                        { key: 'nsf_fee', label: 'Lease NSF fee', render: (r) => (
                            <div className="flex gap-1 items-center">
                                <input className="w-20 p-1 border border-slate-300 rounded text-sm" type="number" min="0" step="0.01"
                                    defaultValue={r.nsf_fee ?? ''} onChange={(e) => setFees({ ...fees, [r.lease_id]: e.target.value })} />
                                <Button variant="subtle" onClick={() => saveFee(r.lease_id)}>Save</Button>
                            </div>
                        ) },
                        { key: 'act', label: '', render: (r) => (isMB ? <Button variant="ghost" onClick={() => clear(r.lease_id)}>Lift hold</Button> : null) },
                    ]} />
            )}
            <p className="text-xs text-slate-500 mt-2">Enter an NSF fee only if the lease provides for one; it is then offered on the returned payment under Payments.</p>
        </Panel>
    );
}

function SettingsTab({ api, isMB, summary, setNotice, reload }) {
    const s = summary.settings;
    const [form, setForm] = useState({
        enabled: s.enabled, card_enabled: s.card_enabled, card_fee_enabled: s.card_fee_enabled,
        card_fee_pct: s.card_fee_pct, card_fee_fixed: s.card_fee_fixed, autopay_enabled: s.autopay_enabled,
        escrow_account_id: s.escrow_account_id ?? '', ach_return_fee: s.ach_return_fee, nsf_auto: s.nsf_auto,
    });
    const [preview, setPreview] = useState(null);
    const trust = (summary.trust_accounts || []).filter((a) => !a.account_type || String(a.account_type).startsWith('trust'));
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
    const example = Math.round((1000 * Number(form.card_fee_pct || 0) / 100 + Number(form.card_fee_fixed || 0)) * 100) / 100;

    const save = async () => {
        try {
            await api.put(`${API}/settings`, form);
            setNotice({ tone: 'good', message: 'Settings saved.' });
            reload();
        } catch (e) { setNotice(errNotice(e)); }
    };
    const runAutopay = async (dryRun) => {
        try {
            const r = await api.post(`${API}/autopay/run`, { dry_run: dryRun });
            setPreview(r);
            if (!dryRun) reload();
        } catch (e) { setNotice(errNotice(e)); }
    };

    const Check = ({ k, label, hint }) => (
        <label className="flex items-start gap-2 mb-3 text-sm">
            <input type="checkbox" checked={!!form[k]} onChange={set(k)} disabled={!isMB} className="mt-1" />
            <span>{label}{hint && <span className="block text-xs text-slate-500">{hint}</span>}</span>
        </label>
    );

    return (
        <Panel title="Settings" subtitle={isMB ? 'Only the managing broker can change these.' : 'Read only: the managing broker changes these.'}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <Check k="enabled" label="Online payments switched on" hint="Tenants see Pay Rent only when this is on and Stripe is set up." />
                    <Select label="Stripe pays out to (trust account)" value={form.escrow_account_id} onChange={set('escrow_account_id')} disabled={!isMB}
                        hint="Must be the same bank account set as the payout account in Stripe. Only properties whose trust account is this one are offered online payment."
                        options={[{ value: '', label: '— choose —' }, ...trust.map((a) => ({ value: a.account_id, label: `${a.account_name} (${a.bank_name || 'bank'})` }))]} />
                    <Check k="autopay_enabled" label="Offer autopay (bank account only)" />
                    <Input label="Stripe ACH return fee charged to the brokerage ($)" type="number" step="0.01" min="0" value={form.ach_return_fee} onChange={set('ach_return_fee')} disabled={!isMB}
                        hint="Booked as a brokerage expense and a trust top-up when a bank payment is returned. Check your Stripe pricing." />
                    <Check k="nsf_auto" label="Charge the lease's NSF fee automatically after a return" hint="Only leases with an NSF fee entered. Off = staff charge it from Payments." />
                </div>
                <div>
                    <Check k="card_enabled" label="Accept cards" />
                    <Check k="card_fee_enabled" label="Charge a card convenience fee" hint="Shown to the tenant before paying, recorded as brokerage income, never as rent." />
                    <Input label="Card fee — percent of the payment" type="number" step="0.01" min="0" max="10" value={form.card_fee_pct} onChange={set('card_fee_pct')} disabled={!isMB} />
                    <Input label="Card fee — fixed amount ($)" type="number" step="0.01" min="0" value={form.card_fee_fixed} onChange={set('card_fee_fixed')} disabled={!isMB}
                        hint={`On $1,000 of rent the tenant would pay ${money(1000 + (form.card_fee_enabled ? example : 0))}. [verify] card-network and Illinois rules on surcharges before switching this on.`} />
                </div>
            </div>
            {isMB && <Button onClick={save}>Save settings</Button>}

            {isMB && (
                <div className="mt-6 border-t border-slate-200 pt-4">
                    <p className="text-sm font-medium text-slate-700 mb-2">Autopay runs every morning with the office automation. Run it by hand:</p>
                    <div className="flex gap-2">
                        <Button variant="ghost" onClick={() => runAutopay(true)}>Preview today's draws</Button>
                        <Button variant="danger" onClick={() => { if (window.confirm('Draw autopay now for every enrolment that is due?')) runAutopay(false); }}>Run now</Button>
                    </div>
                    {preview && (
                        <div className="mt-3 text-sm">
                            <p>{preview.dry_run ? 'Would draw' : 'Drew'}: {preview.drawn.length} · skipped: {preview.skipped.length}</p>
                            <ul className="list-disc list-inside text-xs text-slate-600">
                                {preview.drawn.map((x) => <li key={`d${x.enrollment_id}`}>Lease {x.lease_id}: {money(x.amount)}</li>)}
                                {preview.skipped.map((x, i) => <li key={`s${i}`}>{x.lease_id ? `Lease ${x.lease_id}: ` : ''}{x.reason}</li>)}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </Panel>
    );
}
